import * as React from 'react';
import * as SiteAlertDialog from '@/components/ui/alert-dialog';
import * as OsAlertDialog from '@/OS/components/ui/alert-dialog';

type EmbeddedProp = {
  embedded?: boolean;
};

const EmbeddedAlertDialogContext = React.createContext<boolean | undefined>(undefined);

const detectEmbedded = () => typeof document !== 'undefined' && Boolean(document.querySelector('.mapleos-root'));

const useEmbeddedAlertDialog = (embedded?: boolean) => embedded ?? React.useContext(EmbeddedAlertDialogContext) ?? detectEmbedded();

export function AlertDialog({ embedded, children, ...props }: React.ComponentProps<typeof SiteAlertDialog.AlertDialog> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialog : SiteAlertDialog.AlertDialog) as typeof SiteAlertDialog.AlertDialog;

  return (
    <EmbeddedAlertDialogContext.Provider value={currentEmbedded}>
      <Component {...props}>{children}</Component>
    </EmbeddedAlertDialogContext.Provider>
  );
}

export const AlertDialogTrigger = React.forwardRef<
  React.ElementRef<typeof SiteAlertDialog.AlertDialogTrigger>,
  React.ComponentPropsWithoutRef<typeof SiteAlertDialog.AlertDialogTrigger> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogTrigger : SiteAlertDialog.AlertDialogTrigger) as typeof SiteAlertDialog.AlertDialogTrigger;

  return <Component ref={ref as never} {...props} />;
});
AlertDialogTrigger.displayName = 'OSAwareAlertDialogTrigger';

export function AlertDialogPortal({ embedded, ...props }: React.ComponentProps<typeof SiteAlertDialog.AlertDialogPortal> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogPortal : SiteAlertDialog.AlertDialogPortal) as typeof SiteAlertDialog.AlertDialogPortal;

  return <Component {...props} />;
}

export const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof SiteAlertDialog.AlertDialogOverlay>,
  React.ComponentPropsWithoutRef<typeof SiteAlertDialog.AlertDialogOverlay> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogOverlay : SiteAlertDialog.AlertDialogOverlay) as typeof SiteAlertDialog.AlertDialogOverlay;

  return <Component ref={ref as never} {...props} />;
});
AlertDialogOverlay.displayName = 'OSAwareAlertDialogOverlay';

export const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof SiteAlertDialog.AlertDialogContent>,
  React.ComponentPropsWithoutRef<typeof SiteAlertDialog.AlertDialogContent> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogContent : SiteAlertDialog.AlertDialogContent) as typeof SiteAlertDialog.AlertDialogContent;

  return <Component ref={ref as never} {...props} />;
});
AlertDialogContent.displayName = 'OSAwareAlertDialogContent';

export function AlertDialogHeader({ embedded, ...props }: React.ComponentProps<typeof SiteAlertDialog.AlertDialogHeader> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogHeader : SiteAlertDialog.AlertDialogHeader) as typeof SiteAlertDialog.AlertDialogHeader;

  return <Component {...props} />;
}

export function AlertDialogFooter({ embedded, ...props }: React.ComponentProps<typeof SiteAlertDialog.AlertDialogFooter> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogFooter : SiteAlertDialog.AlertDialogFooter) as typeof SiteAlertDialog.AlertDialogFooter;

  return <Component {...props} />;
}

export const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof SiteAlertDialog.AlertDialogTitle>,
  React.ComponentPropsWithoutRef<typeof SiteAlertDialog.AlertDialogTitle> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogTitle : SiteAlertDialog.AlertDialogTitle) as typeof SiteAlertDialog.AlertDialogTitle;

  return <Component ref={ref as never} {...props} />;
});
AlertDialogTitle.displayName = 'OSAwareAlertDialogTitle';

export const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof SiteAlertDialog.AlertDialogDescription>,
  React.ComponentPropsWithoutRef<typeof SiteAlertDialog.AlertDialogDescription> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogDescription : SiteAlertDialog.AlertDialogDescription) as typeof SiteAlertDialog.AlertDialogDescription;

  return <Component ref={ref as never} {...props} />;
});
AlertDialogDescription.displayName = 'OSAwareAlertDialogDescription';

export const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof SiteAlertDialog.AlertDialogAction>,
  React.ComponentPropsWithoutRef<typeof SiteAlertDialog.AlertDialogAction> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogAction : SiteAlertDialog.AlertDialogAction) as typeof SiteAlertDialog.AlertDialogAction;

  return <Component ref={ref as never} {...props} />;
});
AlertDialogAction.displayName = 'OSAwareAlertDialogAction';

export const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof SiteAlertDialog.AlertDialogCancel>,
  React.ComponentPropsWithoutRef<typeof SiteAlertDialog.AlertDialogCancel> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedAlertDialog(embedded);
  const Component = (currentEmbedded ? OsAlertDialog.AlertDialogCancel : SiteAlertDialog.AlertDialogCancel) as typeof SiteAlertDialog.AlertDialogCancel;

  return <Component ref={ref as never} {...props} />;
});
AlertDialogCancel.displayName = 'OSAwareAlertDialogCancel';
